import { CloudActions } from '@portal/portal-core';
import { call, debounce, put, select } from 'redux-saga/effects';

// `constants/index` (not bare `constants`, which resolves to Node's builtin).
import * as Constants from 'constants/index';
import { selectFilterKeyword } from 'selectors';
import { clearUsers, searchComplete, setUsers } from '../actions';
// `../api` is untyped JS; sendAction resolves as `any`.
import { sendAction } from '../api';
import { transformUser } from '../shared';

// Minimal shape of the superadmin API config passed through to `sendAction`.
interface SuperClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
  timeout?: number;
}

// Single elasticsearch hit as returned in the search response `data` map.
interface UserSearchHit {
  _source: { json: Parameters<typeof transformUser>[0] };
}

// Wrapped response returned by `sendAction` for an elasticsearch user search.
interface SearchUsersResponse {
  succeeded?: boolean;
  data?: Record<string, UserSearchHit>;
}

// Fetch license data on load or after change
export function* fetchSearchUsers(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  try {
    const searchString = (yield select(selectFilterKeyword)) as string;
    const action = CloudActions.elasticsearchUserSearch(searchString, []);
    const res = (yield call(sendAction, config, action)) as SearchUsersResponse;

    if (res.succeeded) {
      yield put(clearUsers());

      const usersArray = Object.values(res.data ?? {}).map((user) =>
        transformUser(user._source.json),
      );
      yield put(setUsers(usersArray));
      yield put(searchComplete(action.requestId));
    } else {
      throw new Error('response failed');
    }
  } catch (error) {
    console.error('Fetch Search Users -- ', error);
    // Runtime intentionally reports no requestId here; the action creator types
    // requestId as `string`, so cast the null through to preserve behavior.
    yield put(
      searchComplete(null as unknown as string, (error as Error).message),
    );
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchFetchSearchUsers(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield debounce(
    500,
    Constants.SuperAdmin.FETCH_ACTION_TYPES.FETCH_SEARCH_USERS,
    fetchSearchUsers,
    config,
  );
}
